/**
 * HTTP client. DOM-free apart from `fetch`, which exists on every target.
 *
 * Two things it does that a bare fetch wrapper does not:
 *
 * - **Refreshes the access token transparently.** A short access token plus a refresh
 *   token is what lets the native shells keep the refresh token in the OS keychain and
 *   the access token in memory only. A single 24-hour token in localStorage -- the
 *   predecessor's design -- is both long-lived and XSS-reachable.
 * - **Unwraps the server's structured error body** into a typed error, so every caller
 *   handles one shape rather than guessing whether `detail` is a string or an object.
 */

const BASE_URL = "/api";

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors: string[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly problems: string[];
  readonly detail: string;

  constructor(problem: Problem) {
    super(problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.detail = problem.detail;
    this.problems = problem.errors;
  }

  /** A single readable line, for a toast. */
  get summary(): string {
    if (this.problems.length > 0) return `${this.message}: ${this.problems[0]}`;
    return this.detail ? `${this.message}: ${this.detail}` : this.message;
  }
}

type TokenSource = () => { access: string | null; refresh: string | null };
type TokenSink = (tokens: { access: string; refresh: string }) => void;
type Logout = () => void;

let readTokens: TokenSource = () => ({ access: null, refresh: null });
let writeTokens: TokenSink = () => {};
let onUnauthorized: Logout = () => {};

/**
 * Wire the client to the auth store.
 *
 * Injected rather than imported so this module stays free of store dependencies and
 * remains testable without a React tree.
 */
export function configureAuth(source: TokenSource, sink: TokenSink, logout: Logout): void {
  readTokens = source;
  writeTokens = sink;
  onUnauthorized = logout;
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  // One refresh at a time: several concurrent 401s must not each spend the refresh
  // token, which would invalidate the others.
  if (refreshInFlight) return refreshInFlight;

  const { refresh } = readTokens();
  if (!refresh) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // In the body, not the query string: a refresh token is valid for 30 days and
        // a URL parameter would land it in proxy logs and browser history.
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { access_token: string; refresh_token: string };
      writeTokens({ access: body.access_token, refresh: body.refresh_token });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function toProblem(response: Response): Promise<Problem> {
  const fallback: Problem = {
    type: "about:blank",
    title: response.statusText || "请求失败",
    status: response.status,
    detail: "",
    errors: [],
  };
  try {
    const body = await response.json();
    if (typeof body === "object" && body !== null && "title" in body) {
      return body as Problem;
    }
    // FastAPI's default shape, for anything not routed through our handlers.
    if (typeof body === "object" && body !== null && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") return { ...fallback, detail };
      return { ...fallback, detail: JSON.stringify(detail) };
    }
    return fallback;
  } catch {
    return fallback;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  retryOn401?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, retryOn401 = true, headers, ...rest } = options;
  const { access } = readTokens();

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && retryOn401) {
    if (await refreshAccessToken()) {
      return request<T>(path, { ...options, retryOn401: false });
    }
    onUnauthorized();
  }

  if (!response.ok) {
    throw new ApiError(await toProblem(response));
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: "POST", body }),
  put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: "PATCH", body }),
  delete: <T>(path: string, init?: RequestOptions) =>
    request<T>(path, { ...init, method: "DELETE" }),
};

/** An SSE payload, which the DOM types leave as `any`. */
function textOf(event: Event): string {
  const data: unknown = (event as MessageEvent).data;
  return typeof data === "string" ? data : "";
}

/**
 * Subscribe to a server-sent progress stream.
 *
 * The token goes in the query string because `EventSource` cannot set headers. The
 * server restricts that allowance to paths ending in `/stream`, so a token cannot leak
 * from an ordinary page's referrer.
 */
export function subscribeToStream(
  path: string,
  handlers: {
    onMessage: (data: string) => void;
    onProgress?: (data: string) => void;
    onDone?: (status: string) => void;
    onError?: () => void;
  },
): () => void {
  const { access } = readTokens();
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (access) url.searchParams.set("access_token", access);

  const source = new EventSource(url.toString());
  source.addEventListener("message", (event) => handlers.onMessage(textOf(event)));
  source.addEventListener("progress", (event) => handlers.onProgress?.(textOf(event)));
  source.addEventListener("done", (event) => {
    try {
      const payload = JSON.parse(textOf(event)) as { status: string };
      handlers.onDone?.(payload.status);
    } catch {
      handlers.onDone?.("unknown");
    }
    source.close();
  });
  source.addEventListener("error", () => {
    handlers.onError?.();
    source.close();
  });

  return () => source.close();
}
